import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { useClickOutSide } from "lib/hooks/useClickOutside";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "lib/store/useAppStore";
import { useTranslation } from "react-i18next";
import { PostCreateProject } from "lib/api/postCreateProject";
import { getProjectList } from "lib/api/getProjectList";
import { postUpdateProject } from "lib/api/postUpdateProject";
import { dateformat } from "lib/project/dateFormat";
import { useCheckBoxGroup } from "lib/hooks/useCheckBoxGroup";
import { deleteProject } from "lib/api/deleteProject";
import ProjectSearchBox from "components/project/ProjectSearchBox";
import DateInput from "components/project/DateInput";
import ConfirmDeleteDialog from "components/project/ConfirmDeleteDialog";
import Spinner from "components/Spinner";
import HelpTip from "components/HelpTip";
import { FolderPlus, Network, X } from "lucide-react";
import { ProjectTreePathEditor } from "components/project/ProjectTreeEditor";
import { normalizeProjectPaths } from "lib/utils/projectNodePaths";
import "css/projectCreateDialog.css";
import {
  focusProjectCreateField,
  getFirstEmptyProjectCreateField,
  showProjectCreateRequiredAlert,
} from "lib/utils/projectCreateFormValidation";

const PROJECT_OVERVIEW_PREFS_KEY = "checklab.projectOverview.listPrefs.v1";

const DEFAULT_PROJECT_LIST_PREFS = {
  projectScope: "all",
  sortDir: "desc",
};

const PROJECT_SCOPE_FILTER_OPTIONS = [
  { value: "all", label: "전체" },
  { value: "mine", label: "내 프로젝트" },
  { value: "personal", label: "개인 프로젝트" },
  { value: "organization", label: "조직 프로젝트" },
];

const PROJECT_SORT_OPTIONS = [
  { value: "desc", label: "생성일 최신순" },
  { value: "asc", label: "생성일 오래된순" },
];

const PROJECT_SCOPE_OPTIONS = [
  { value: "personal", label: "나만 보기" },
  { value: "organization", label: "조직 공개" },
];

function normalizeProjectScope(value) {
  if (
    value === "mine" ||
    value === "personal" ||
    value === "organization" ||
    value === "all"
  ) {
    return value;
  }

  return DEFAULT_PROJECT_LIST_PREFS.projectScope;
}

function normalizeProjectSortDir(value) {
  return value === "asc" ? "asc" : DEFAULT_PROJECT_LIST_PREFS.sortDir;
}

function loadProjectOverviewPrefs() {
  if (typeof window === "undefined") return DEFAULT_PROJECT_LIST_PREFS;

  try {
    const raw = window.localStorage.getItem(PROJECT_OVERVIEW_PREFS_KEY);
    if (!raw) return DEFAULT_PROJECT_LIST_PREFS;

    const parsed = JSON.parse(raw);
    return {
      projectScope: normalizeProjectScope(parsed?.projectScope),
      sortDir: normalizeProjectSortDir(parsed?.sortDir),
    };
  } catch {
    return DEFAULT_PROJECT_LIST_PREFS;
  }
}

function saveProjectOverviewPrefs(prefs) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      PROJECT_OVERVIEW_PREFS_KEY,
      JSON.stringify({
        projectScope: normalizeProjectScope(prefs?.projectScope),
        sortDir: normalizeProjectSortDir(prefs?.sortDir),
      }),
    );
  } catch {
    // Ignore storage failures so the list can still be used.
  }
}

function getProjectScope(project) {
  return project?.organization_id || project?.organizationId
    ? "organization"
    : "personal";
}

function isSameId(left, right) {
  if (!left || !right) return false;
  return String(left).toLowerCase() === String(right).toLowerCase();
}

// _________________ProjectOverview Container _________________
export default function ProjectOverviewPage() {
  const user = useAppStore((s) => s.user);
  const { id } = user ?? {};
  const { t } = useTranslation();
  const popupRef = useRef();
  const [isCategorieSelect, setIsCategorieSelect] = useState(() =>
    String(new Date().getFullYear()),
  );
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [projects, setProjects] = useState(null);
  const [soltedProjects, setSoltedProjects] = useState(null);
  const fetchProjectTree = useAppStore((s) => s.fetchProjectTree);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const setLastSelectedProject = useAppStore((s) => s.setLastSelectedProject);
  const [yearList, setYearList] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const showAlert = useAppStore((s) => s.showAlert);
  const [listPrefs, setListPrefs] = useState(loadProjectOverviewPrefs);
  const hasUserOrganization = Boolean(user?.organization_id);

  const selectableProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    return projects.filter((project) => isSameId(project?.user_id, id));
  }, [id, projects]);

  const {
    checkedItems,
    setCheckedItems,
    isAllChecked,
    handleToggleItem,
    handleToggleAll,
  } = useCheckBoxGroup(selectableProjects);
  const selectedProjects = useMemo(() => {
    if (!Array.isArray(projects) || !Array.isArray(checkedItems)) return [];
    return projects.filter((project) => checkedItems.includes(project?.id));
  }, [projects, checkedItems]);

  useEffect(() => {
    saveProjectOverviewPrefs(listPrefs);
  }, [listPrefs]);

  const fetchProjects = useCallback(async () => {
    if (!id) return;

    const { success, projects, yearList } = await getProjectList({
      year: isCategorieSelect,
      keyword: searchKeyword,
      projectScope: listPrefs.projectScope,
      sortDir: listPrefs.sortDir,
    });

    if (success) {
      setProjects(projects);
      setYearList(yearList ?? []);
      setSoltedProjects(projects);
      setCheckedItems([]);
    }
  }, [
    id,
    isCategorieSelect,
    listPrefs.projectScope,
    listPrefs.sortDir,
    searchKeyword,
    setCheckedItems,
  ]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handlePopupClose = () => {
    setIsPopupOpen(false);
  };

  const handleProjectScopeFilterChange = (event) => {
    setListPrefs((prev) => ({
      ...prev,
      projectScope: normalizeProjectScope(event.target.value),
    }));
  };

  const handleProjectSortChange = (event) => {
    setListPrefs((prev) => ({
      ...prev,
      sortDir: normalizeProjectSortDir(event.target.value),
    }));
  };

  const handleProjectScopeChange = useCallback(
    async (project, nextScope) => {
      if (!project?.id) return;

      const normalizedScope = normalizeProjectScope(nextScope);
      if (normalizedScope === "all") return;

      const result = await postUpdateProject({
        id: project.id,
        project_scope: normalizedScope,
      });

      if (!result?.success) {
        showAlert({
          text: result?.message ?? "공개범위 변경에 실패했습니다.",
          messageKey: result?.messageKey,
          messageParams: result?.messageParams,
          success: false,
          autoClose: true,
        });
        return;
      }

      await fetchProjects();
      await fetchProjectTree(id, project.id);

      showAlert({
        text: result?.message ?? "공개범위를 변경했습니다.",
        messageKey: result?.messageKey,
        messageParams: result?.messageParams,
        success: true,
        autoClose: true,
      });
    },
    [fetchProjectTree, fetchProjects, id, showAlert],
  );

  const handleDeleteProject = async (checkedItems) => {
    const {
      success,
      message,
      messageKey,
      messageParams,
      new_last_selected_project,
      new_last_selected_project_name,
    } = await deleteProject(checkedItems, id);

    if (!success) {
      showAlert({
        text: message,
        messageKey,
        messageParams,
        success: false,
        autoClose: true,
      });
      return;
    }

    setLastSelectedProject(
      new_last_selected_project,
      new_last_selected_project_name,
    );

    // ───────────────────────────────────────
    // STEP 1 : 프로젝트 리스트 재조회
    // ───────────────────────────────────────

    if (success) {
      await fetchProjectTree(id, new_last_selected_project);
      await fetchProjects();
      setIsConfirmModalOpen(false);
    }
    showAlert({
      text: message,
      messageKey,
      messageParams,
      success: success,
      autoClose: true,
    });
  };

  return (
    <section className="projectOverview content">
      <div className="helpTitleRow">
        <h2>{t("projectOverview.title")}</h2>
        <HelpTip tipKey={"GET_PROJECTLIST"} />
      </div>
      <div>
        <div>
          <ProjectSearchBox
            searchKeyword={searchKeyword}
            onSearchChange={setSearchKeyword}
            selectedYear={isCategorieSelect}
            onYearChange={setIsCategorieSelect}
            yearList={yearList}
            placeholder={t("projectOverview.projectList.projectNameLabel")}
          />
          <div className="projectOverview__filterControls">
            <label className="projectOverview__filterField">
              <span>범위</span>
              <select
                value={listPrefs.projectScope}
                onChange={handleProjectScopeFilterChange}
              >
                {PROJECT_SCOPE_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="projectOverview__filterField">
              <span>정렬</span>
              <select
                value={listPrefs.sortDir}
                onChange={handleProjectSortChange}
              >
                {PROJECT_SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {checkedItems.length !== 0 && Array.isArray(checkedItems) && (
            <button
              onClick={() => setIsConfirmModalOpen(true)}
              className="btn-bg-blue projectOverview__deleteButton"
            >
              {t("projectOverview.deleteButton")}
            </button>
          )}
        </div>
        <button
          className="btn-bg-blue projectOverview__createButton"
          onClick={() => setIsPopupOpen(true)}
        >
          {t("projectOverview.createProjectButton")}
        </button>
      </div>
      {isPopupOpen && (
        <CreateProject
          user_id={id}
          popupRef={popupRef}
          handlePopupClose={handlePopupClose}
          fetchProjects={fetchProjects}
          fetchProjectTree={fetchProjectTree}
          setLastSelectedProject={setLastSelectedProject}
          showAlert={showAlert}
          hasUserOrganization={hasUserOrganization}
          t={t}
        />
      )}

      {soltedProjects ? (
        <ProjectList
          checkedItems={checkedItems}
          handleToggleAll={handleToggleAll}
          handleToggleItem={handleToggleItem}
          soltedProjects={soltedProjects}
          isAllChecked={isAllChecked}
          currentUserId={id}
          hasUserOrganization={hasUserOrganization}
          onProjectScopeChange={handleProjectScopeChange}
          setLastSelectedProject={setLastSelectedProject}
          t={t}
        />
      ) : (
        <Spinner />
      )}
      {isConfirmModalOpen && (
        <ConfirmDeleteDialog
          onDelete={handleDeleteProject}
          checkedItems={checkedItems}
          selectedProjects={selectedProjects}
          setIsConfirmModalOpen={setIsConfirmModalOpen}
        />
      )}
    </section>
  );
}

// _________________  프로젝트 생성 popup _________________

export function CreateProject({
  fetchProjects,
  handlePopupClose,
  popupRef = "",
  user_id,
  fetchProjectTree,
  setLastSelectedProject,
  showAlert,
  hasUserOrganization,
  t,
}) {
  const [treePaths, setTreePaths] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  useClickOutSide([popupRef], () => { if (!savingRef.current) handlePopupClose(); });
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRefs.project_name.current?.focus();
    return () => { document.body.style.overflow = overflow; previous?.focus?.(); };
    // Focus only when opening the dialog, not while editing fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState({
    project_name: "",
    contract_name: "",
    period_start: "",
    period_end: "",
    contractor: "",
    participants: "",
    purpose: "",
    project_scope: "personal",
  });

  const inputRefs = {
    project_name: useRef(null),
    contract_name: useRef(null),
    period_start: useRef(null),
    period_end: useRef(null),
    contractor: useRef(null),
    participants: useRef(null),
    purpose: useRef(null),
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setForm({
      ...form,
      [id]: value,
    });
  };

  const handleSummit = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (savingRef.current) return;

    // 빈 값 검증 (첫 번째 빈 필드에 포커스)
    const emptyField = getFirstEmptyProjectCreateField(form);

    if (emptyField) {
      showProjectCreateRequiredAlert({
        showAlert,
        inputRefs,
        emptyField,
      });
      return;
    }

    const start = new Date(form.period_start);
    const end = new Date(form.period_end);
    if (form.period_start && form.period_end && end < start) {
      showAlert({
        textKey: "api.project.create.validation.invalidPeriod",
        success: false,
        autoClose: false,
      });
      focusProjectCreateField(inputRefs, "period_end");
      return;
    }

    savingRef.current = true;
    setIsSaving(true);
    try {
    const {
      success,
      message,
      messageKey,
      messageParams,
      project_id,
      project_name,
    } = await PostCreateProject({
      ...form,
      project_scope: hasUserOrganization ? form.project_scope : "personal",
      tree_paths: normalizeProjectPaths(treePaths),
    });

    showAlert({
      text: message,
      messageKey,
      messageParams,
      success: success,
      autoClose: true,
    });

    if (success) {
      setLastSelectedProject(project_id, project_name);
      handlePopupClose();
      await fetchProjects();
      await fetchProjectTree(user_id, project_id);
    }
    } catch {
      showAlert({ text: "요청 처리 또는 목록 갱신에 실패했습니다. 프로젝트 목록을 확인해 주세요.", success: false, autoClose: false });
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <div className="projectCreateDialogBackdrop">
      <div ref={popupRef} className="projectCreateDialog" role="dialog" aria-modal="true" aria-labelledby="projectCreateDialogTitle" aria-busy={isSaving}
        onKeyDown={event => {
          if (event.key === "Escape") { event.stopPropagation(); if (!savingRef.current) handlePopupClose(); }
          if (event.key === "Tab") {
            const controls = [...event.currentTarget.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]')].filter(element => element.getClientRects().length);
            const first = controls[0], last = controls[controls.length - 1];
            if (!first) { event.preventDefault(); return; }
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
          }
        }}>
        <div className="projectCreateDialog__header">
          <div><h2 id="projectCreateDialogTitle"><FolderPlus size={20} aria-hidden="true" /><span>{t("projectOverview.createProjectPopup.title")}</span></h2>
            <p>프로젝트 정보를 입력하세요. 건물·층·위치는 지금 등록하거나 나중에 추가할 수 있습니다.</p></div>
          <button onClick={handlePopupClose} type="button" disabled={isSaving} aria-label={t("projectOverview.createProjectPopup.closeButton")}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <form onSubmit={(e) => handleSummit(e)}>
          <div className="projectCreateDialog__body">
          <fieldset className="projectCreateDialog__fields" disabled={isSaving}>
          <legend>프로젝트 정보 <small>필수 입력</small></legend>
          <label htmlFor="project_name">
            {t("projectOverview.createProjectPopup.projectNameLabel")}
          </label>
          <input
            ref={inputRefs.project_name}
            onChange={(e) => handleChange(e)}
            value={form.project_name}
            id="project_name"
            type="text"
            placeholder={t(
              "projectOverview.createProjectPopup.projectNamePlaceholder",
            )}
          />

          <label htmlFor="project_scope">공개 범위</label>
          <select
            className="createProject__scopeSelect"
            onChange={(e) => handleChange(e)}
            value={form.project_scope}
            id="project_scope"
          >
            <option value="personal">나만 보기</option>
            <option value="organization" disabled={!hasUserOrganization}>
              조직 공개
            </option>
          </select>

          <label htmlFor="contract_name">
            {t("projectOverview.createProjectPopup.contractNameLabel")}
          </label>
          <input
            ref={inputRefs.contract_name}
            onChange={(e) => handleChange(e)}
            value={form.contract_name}
            id="contract_name"
            type="text"
            placeholder={t(
              "projectOverview.createProjectPopup.contractNamePlaceholder",
            )}
          />

          <label htmlFor="period_start">
            {t("projectOverview.createProjectPopup.periodLabel")}
          </label>
          <div className="projectCreateDialog__dates">
            <DateInput
              ref={inputRefs.period_start}
              setForm={setForm}
              form={form}
              value={form.period_start}
              htmlFor={"period_start"}
            />
            <span> ~ </span>
            <DateInput
              ref={inputRefs.period_end}
              setForm={setForm}
              form={form}
              value={form.period_end}
              htmlFor={"period_end"}
            />
          </div>
          <label htmlFor="contractor">
            {t("projectOverview.createProjectPopup.contractorLabel")}
          </label>
          <input
            ref={inputRefs.contractor}
            onChange={(e) => handleChange(e)}
            value={form.contractor}
            id="contractor"
            type="text"
            placeholder={t(
              "projectOverview.createProjectPopup.contractorPlaceholder",
            )}
          />

          <label htmlFor="participants">
            {t("projectOverview.createProjectPopup.participantsLabel")}
          </label>
          <input
            ref={inputRefs.participants}
            onChange={(e) => handleChange(e)}
            value={form.participants}
            id="participants"
            type="text"
            maxLength={500}
            placeholder={t(
              "projectOverview.createProjectPopup.participantsPlaceholder",
            )}
          />

          <label htmlFor="purpose">
            {t("projectOverview.createProjectPopup.purposeLabel")}
          </label>
          <textarea
            ref={inputRefs.purpose}
            onChange={(e) => handleChange(e)}
            value={form.purpose}
            id="purpose"
            placeholder={t(
              "projectOverview.createProjectPopup.purposePlaceholder",
            )}
          />
          </fieldset>
          <details className="projectCreateDialog__tree">
            <summary><Network size={18} aria-hidden="true" /><strong>현장 경로 설정</strong><span>선택 사항{normalizeProjectPaths(treePaths).length ? ` · ${normalizeProjectPaths(treePaths).length}개 경로` : ""}</span></summary>
            <p>건물 → 층 → 위치를 미리 구성하면 파일 업로드 시 해당 경로를 선택할 수 있습니다. 입력하지 않아도 생성됩니다.</p>
            <ProjectTreePathEditor paths={treePaths} onChange={setTreePaths} disabled={isSaving} />
          </details>
          </div>
          <footer className="projectCreateDialog__footer">
          <button type="button" disabled={isSaving} onClick={handlePopupClose}>취소</button>
          <button className="btn-bg-blue" type="submit" disabled={isSaving}>
            {isSaving ? "생성 중…" : t("projectOverview.createProjectPopup.createSubmitButton")}
          </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

// _________________ project Table _________________
export function ProjectList({
  soltedProjects,
  checkedItems,
  handleToggleAll,
  handleToggleItem,
  isAllChecked,
  currentUserId,
  hasUserOrganization,
  onProjectScopeChange,
  setLastSelectedProject,
  t,
}) {
  const navigate = useNavigate();
  const labels = {
    select: t("projectOverview.projectList.selectLabel"),
    created: t("projectOverview.projectList.createdDateLabel"),
    creator: "생성자",
    projectName: t("projectOverview.projectList.projectNameLabel"),
    contractName: t("projectOverview.projectList.contractNameLabel"),
    contractor: t("projectOverview.projectList.contractorLabel"),
    period: t("projectOverview.projectList.periodLabel"),
    scope: "공개 범위",
  };
  const hasSelectableProjects =
    Array.isArray(soltedProjects) &&
    soltedProjects.some((project) => isSameId(project?.user_id, currentUserId));

  return (
    <table className="projectList">
      <thead>
        <tr>
          <th>
            <div className="projectList__checkCell">
              <input
                id="allChecked"
                onChange={handleToggleAll}
                checked={hasSelectableProjects && isAllChecked}
                disabled={!hasSelectableProjects}
                type="checkbox"
              />
              <label htmlFor="allChecked">{labels.select}</label>
            </div>
          </th>
          <th>{labels.created}</th>
          <th>{labels.creator}</th>
          <th>{labels.projectName}</th>
          <th>{labels.contractName}</th>
          <th>{labels.contractor}</th>
          <th>{labels.scope}</th>
          <th>{labels.period}</th>
        </tr>
      </thead>
      <tbody>
        {Array.isArray(soltedProjects) &&
          soltedProjects.map((item) => {
            const formatted = {
              created: dateformat(item?.created_at).hasTimeDate,
              start: dateformat(item?.period_start).hasDayDate,
              end: dateformat(item?.period_end).hasDayDate,
            };
            const projectScope = getProjectScope(item);
            const isOwner = isSameId(item?.user_id, currentUserId);
            const scopeBadgeLabel =
              projectScope === "organization" ? "조직" : "개인";

            return (
              <tr
                key={item?.id}
                tabIndex={0}
                role="button"
                onClick={() => {
                  setLastSelectedProject(item?.id, item?.project_name);
                  navigate(`/project/${item.id}`);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setLastSelectedProject(item?.id, item?.project_name);
                    navigate(`/project/${item.id}`);
                  }
                }}
              >
                <td data-label={labels.select}>
                  <input
                    aria-label={`${item?.project_name ?? ""} ${labels.select}`}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleToggleItem(item?.id)}
                    checked={isOwner && checkedItems.includes(item?.id)}
                    disabled={!isOwner}
                    type="checkbox"
                  />
                </td>
                <td data-label={labels.created}>{formatted.created}</td>
                <td data-label={labels.creator}>{item?.creator_name || "-"}</td>
                <td data-label={labels.projectName}>{item?.project_name}</td>
                <td data-label={labels.contractName}>{item?.contract_name}</td>
                <td data-label={labels.contractor}>{item?.contractor}</td>
                <td data-label={labels.scope}>
                  <div
                    className="projectScopeCell"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <span
                      className={`projectScopeBadge projectScopeBadge--${projectScope}`}
                    >
                      {scopeBadgeLabel}
                    </span>
                    <select
                      className="projectScopeSelect"
                      value={projectScope}
                      disabled={!isOwner}
                      aria-label={`${item?.project_name ?? ""} 공개 범위`}
                      onChange={(e) =>
                        onProjectScopeChange?.(item, e.target.value)
                      }
                    >
                      {PROJECT_SCOPE_OPTIONS.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          disabled={
                            option.value === "organization" &&
                            !hasUserOrganization
                          }
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>
                <td data-label={labels.period}>
                  {`${formatted.start} ~ ${formatted.end}`}
                </td>
              </tr>
            );
          })}
      </tbody>
    </table>
  );
}
